import React, { useState } from 'react';
import { X, UserPlus, Mail, Trash2, LogOut, Crown, Users } from 'lucide-react';
import './ShareModal.css';

export function ShareListModal({ 
  list, 
  currentUser,
  onClose, 
  onInviteMember,
  onRemoveMember,
  onLeaveList
}) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInvite = async () => {
    setError('');
    
    if (!email || !email.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      await onInviteMember(list.id, email.toLowerCase().trim());
      setEmail('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = (memberId) => {
    const member = list.memberDetails[memberId];
    if (window.confirm(`Remove ${member.name} from "${list.name}"?`)) {
      onRemoveMember(list.id, memberId);
    }
  };

  const handleLeave = () => {
    if (window.confirm(`Leave "${list.name}"? You won't be able to access it unless the owner invites you again.`)) {
      onLeaveList(list.id);
    }
  };

  const isOwner = list.ownerId === currentUser.id;
  const members = list.members || [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal share-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="share-modal-title">
            <Users size={24} />
            <h3>Share "{list.name}"</h3>
          </div>
          <button className="btn-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {isOwner && (
          <div className="share-invite-section">
            <label>Invite by Email</label>
            <div className="invite-input-group">
              <div className="input-with-icon">
                <Mail size={20} />
                <input
                  type="email"
                  placeholder="friend@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleInvite()}
                  disabled={loading}
                />
              </div>
              <button 
                className="btn-primary btn-invite" 
                onClick={handleInvite}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Inviting...
                  </>
                ) : (
                  <>
                    <UserPlus size={18} />
                    Invite
                  </>
                )}
              </button>
            </div>
            {error && (
              <div className="invite-error">
                {error}
              </div>
            )}
          </div>
        )}

        <div className="members-section">
          <h4>Members ({members.length})</h4>
          <div className="members-list">
            {members.map(memberId => {
              const member = list.memberDetails[memberId];
              const isMemberOwner = memberId === list.ownerId;
              const isCurrentUser = memberId === currentUser.id;

              return (
                <div key={memberId} className="member-item">
                  <div className="member-info">
                    <div 
                      className="member-avatar" 
                      style={{ backgroundColor: member.color }}
                    >
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="member-details">
                      <span className="member-name">
                        {member.name}
                        {isCurrentUser && <span className="you-badge"> (You)</span>}
                      </span>
                      <span className="member-role">
                        {isMemberOwner ? (
                          <>
                            <Crown size={12} /> Owner
                          </>
                        ) : (
                          'Member'
                        )}
                      </span>
                    </div>
                  </div>

                  {isOwner && !isMemberOwner && (
                    <button
                      className="btn-icon-danger"
                      onClick={() => handleRemove(memberId)}
                      title="Remove member"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {!isOwner && (
          <div className="leave-section">
            <button className="btn-danger" onClick={handleLeave}>
              <LogOut size={18} />
              Leave List
            </button>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
